import { config } from "core/config";
import { GatewaysContext } from "core/gateways-context";
import {
  LocationGateway,
  ProviderGateway,
  UnavailabilityGateway
} from "core/gateways/practice";
import { TenantGateway } from "core/gateways/tenant";
import { UserGateway } from "core/gateways/user/UserGateway";
import { ExceptionFilter } from "core/middleware";
import { TenantInterceptorMiddleware } from "core/middleware/tenant-interceptor.middleware";
import { NO_AUTH } from "core/types/api.constants";
import { PrOSApiSchema } from "core/types/api.interface";
import { FC, PropsWithChildren } from "react";

import {
  AuthorizationMiddleware,
  AxiosHelperBase,
  EndpointConfig
} from "@bps/http-client";
import { getAccessToken } from "@bps/msal";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { BpIdGateway } from "./gateways/bp-id/bp-id.gateway";
import { PltCatalogGateway } from "./gateways/plt-catalog/plt-catalog.gateway";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: config.query.staleTimeMillis
    }
  }
});

type ReactQueryProvider = PropsWithChildren;

export const ReactQueryProvider: FC<ReactQueryProvider> = ({ children }) => {
  const endPoints: EndpointConfig<PrOSApiSchema> = {
    practice: { baseURL: config.apiUrl },
    tenant: { baseURL: config.tgApiUrl },
    bpId: { baseURL: config.bpIdApiBaseUrl },
    pltCatalog: { baseURL: config.pltCatalogBaseUrl }
  };

  const axiosHelper = new AxiosHelperBase<PrOSApiSchema>(endPoints, {
    request: [
      new AuthorizationMiddleware(() => getAccessToken(true), NO_AUTH),
      new TenantInterceptorMiddleware()
    ],
    respond: [new ExceptionFilter()]
  });

  const api = axiosHelper.initialize();

  const locationApi = new LocationGateway(api.practice);
  const providerApi = new ProviderGateway(api.practice);
  const unavailabilityApi = new UnavailabilityGateway(api.practice);

  const userApi = new UserGateway(api.tenant);
  const tenantApi = new TenantGateway(api.tenant);
  const bpIdApi = new BpIdGateway(api.bpId);
  const pltCatalogApi = new PltCatalogGateway(api.pltCatalog);

  return (
    <GatewaysContext.Provider
      value={{
        locationApi,
        providerApi,
        unavailabilityApi,
        userApi,
        tenantApi,
        bpIdApi,
        pltCatalogApi
      }}
    >
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </GatewaysContext.Provider>
  );
};
